/**
 * Naming a device, purely. The browser has no device name to ask for, so the
 * app suggests one from what `navigator` does say and lets the user correct
 * it — a name is for recognising a machine in a list, not for identifying it.
 *
 * Nothing here touches `navigator` itself: the hints are passed in, so the
 * rules are testable in node and the UI decides what it can read.
 */
import type { DeviceIdentity } from '../types';

export const DEVICE_NAME_MAX = 60;
export const FALLBACK_DEVICE_NAME = 'Den här webbläsaren';

export interface DeviceNameHints {
  /** `navigator.userAgentData.platform` or `navigator.platform`. */
  platform?: string | undefined;
  /** `navigator.userAgentData.brands`. */
  brands?: readonly { brand: string }[] | undefined;
  userAgent?: string | undefined;
  /** `navigator.userAgentData.mobile`. */
  mobile?: boolean | undefined;
}

/** Chromium reports a deliberately meaningless brand alongside the real ones. */
const NOISE_BRAND = /not[^a-z]*a[^a-z]*brand/i;

function brandName(brands: readonly { brand: string }[] | undefined): string | null {
  const real = (brands ?? []).map((b) => b.brand).filter((b) => !NOISE_BRAND.test(b));
  // "Chromium" is the engine every one of them shares; prefer anything else.
  return real.find((b) => !/^chromium$/i.test(b)) ?? real[0] ?? null;
}

/** Browser name from the user agent string. Order matters: each one lies about the ones above it. */
function browserFromUserAgent(ua: string): string | null {
  if (/\bEdgi?A?\//.test(ua)) return 'Edge';
  if (/\bOPR\/|\bOpera\//.test(ua)) return 'Opera';
  if (/\bFirefox\/|\bFxiOS\//.test(ua)) return 'Firefox';
  if (/\bCriOS\//.test(ua)) return 'Chrome';
  if (/\bChrome\//.test(ua)) return 'Chrome';
  if (/\bSafari\//.test(ua)) return 'Safari';
  return null;
}

function platformLabel(hints: DeviceNameHints): string | null {
  const raw = `${hints.platform ?? ''} ${hints.userAgent ?? ''}`;
  if (/iphone/i.test(raw)) return 'iPhone';
  if (/ipad/i.test(raw)) return 'iPad';
  if (/android/i.test(raw)) return hints.mobile === false ? 'Android-platta' : 'Android';
  if (/mac/i.test(raw)) return 'Mac';
  if (/win/i.test(raw)) return 'Windows';
  if (/cros|chrome ?os/i.test(raw)) return 'Chromebook';
  if (/linux/i.test(raw)) return 'Linux';
  return null;
}

/**
 * "Chrome på Mac", "Safari på iPhone", or the fallback when the browser says
 * nothing useful. Never throws, never empty.
 */
export function suggestDeviceName(hints: DeviceNameHints = {}): string {
  const browser = brandName(hints.brands) ?? browserFromUserAgent(hints.userAgent ?? '');
  const platform = platformLabel(hints);
  if (browser && platform) return normalizeDeviceName(`${browser} på ${platform}`);
  return normalizeDeviceName(browser ?? platform ?? FALLBACK_DEVICE_NAME);
}

/** Trim, collapse whitespace, cap the length; empty input falls back. */
export function normalizeDeviceName(raw: string): string {
  const name = raw.replace(/\s+/g, ' ').trim().slice(0, DEVICE_NAME_MAX);
  return name || FALLBACK_DEVICE_NAME;
}

/** Short, stable form for a UI that has to show the id itself. */
export function shortDeviceId(device: Pick<DeviceIdentity, 'id'>): string {
  return device.id.slice(0, 8);
}
